import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { XCircleFill } from 'react-bootstrap-icons';
import { UserDetails } from '../../../types/schemas/abc';
import { useEvents } from '../../../hooks/useEvents';
import { useToast } from '../../../contextManager/toast.context';
import { loadStripe } from '@stripe/stripe-js';
import { usePayment } from '../../../hooks/usePayment';
import { useQueryClient } from '@tanstack/react-query';
import {
  CloseButton, ContentWrapper,
  ModalContainer, ModalFooter, ModalHeader, ModalOverlay,
  PrimaryButton, SecondaryButton, StepContent
} from '../../../styles/lesson/lesson.style';
import {v4 as uuidv4} from 'uuid';


// Import the new state management
import {
  LessonCreationProvider,
  useLessonCreationState,
  useLessonCreationDispatch,
  getInitialState,
  Step,
  Frequency,
  LessonItem,
  OTHER,
} from './lessonCreation.state';

// Import Step Components
// (We will only build Step 1 for now)
import { Step0_IntroChoice } from './steps/Step0_IntroChoice';
import { Step1_SubjectLevel } from './steps/Step1_SubjectLevel';
import { Step2_FirstLesson } from './steps/Step2_FirstLesson';
import { Step6_Payment } from './steps/Step6_Payment';
import { Step5_LessonPlan } from './steps/Step5_LessonPlan';
import { Step4_Frequency } from './steps/Step4_Frequency';
import { Step3_RecursChoice } from './steps/Step3_RecursChoice';
import { useContext } from 'react';
import { AxiosContext } from '../../../contextManager/axios.context';
import { endpoints } from '../../../constants/endpoints';
import { wallClockToISO, wallClockEndToISO } from '../../../utils/timezones';
import { BusySlot } from './lessonCreation.state';
// import { Step2_FirstLesson } from './steps/Step2_FirstLesson';
// import { Step3_RecursChoice } from './steps/Step3_RecursChoice';
// import { Step4_Frequency } from './steps/Step4_Frequency';
// import { Step5_LessonPlan } from './steps/Step5_LessonPlan';
// import { Step6_Payment } from './steps/Step6_Payment';

const PAYMENT_FORM_ID = 'stripe-payment-form';

// --- Utils (Move to a utils file later) ---
const formatGB = (d: Date) =>
  new Intl.DateTimeFormat('en-GB', { dateStyle: 'full', timeStyle: 'short' }).format(d);

const addDaysToDateStr = (dateStr: string, days: number): string => {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
};

const addMonthsToDateStr = (dateStr: string, months: number): string => {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1 + months, d));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
};

const generateOccurrences = (
  startDate: string,
  startTime: string,
  endTime: string,
  lessonTitle: string,
  freq: Frequency | null,
  count: number,
  singleIfNoFreq = false
): LessonItem[] => {
  if (!startDate || !startTime) return [];
  const out: LessonItem[] = [];
  const n = freq ? Math.max(1, count) : (singleIfNoFreq ? 1 : Math.max(1, count));

  for (let i = 0; i < n; i++) {
    let date = startDate;
    if (freq && i > 0) {
      switch (freq) {
        case 'daily':    date = addDaysToDateStr(startDate, i); break;
        case 'weekly':   date = addDaysToDateStr(startDate, 7 * i); break;
        case 'biweekly': date = addDaysToDateStr(startDate, 14 * i); break;
        case 'monthly':  date = addMonthsToDateStr(startDate, i); break;
      }
    }

    const finalTitle = n > 1
      ? `Lesson ${i + 1}: ${lessonTitle}`
      : lessonTitle;

    out.push({
      id: `${date}-${i}-${Math.random().toString(36).slice(2, 7)}`,
      date,
      time: startTime,
      endTime,
      title: finalTitle,
    });
  }
  return out;
};


// --- Main Modal Props ---
interface LessonCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  tutorId: string | null;
  tutorName: string | null;
  user: UserDetails;
  tutorHourlyRate: number;
  tutorTimezone?: string;
  tutorAvailability?: import('../../../types/schemas/abc').AvailabilityObject | null;
  existingEvents?: import('../../../hooks/useEvents').CalendarEvent[];
  tutorOffersIntroCall?: boolean;
  onBackFromStart?: () => void;
  initialInputTz?: string;
}

const toReadableHeader = (s: Step): string => {
  switch (s) {
    case 0: return 'Schedule an Intro Call?';
    case 1: return 'Select Lesson Subject and Level';
    case 2: return 'Schedule First Lesson';
    case 3: return 'Does it Recur?';
    case 4: return 'How Often?';
    case 5: return 'Your Lesson Plan';
    case 6: return 'Secure Payment';
  }
};

/**
 * This is the inner component that has access to the context.
 * The main modal component is split to allow the Provider
 * to be set up *before* its consumers are rendered.
 */
const ModalContent: React.FC<{
  onClose: () => void;
  initialState: ReturnType<typeof getInitialState>;
  skipIntro: boolean;
  onBackFromStart?: () => void;
}> = ({ onClose, initialState, skipIntro, onBackFromStart }) => {
  // --- Local Step State ---
  const [step, setStep] = useState<Step>(skipIntro ? 1 : 0);

  // --- Global State & Dispatch from Context ---
  const state = useLessonCreationState();
  const dispatch = useLessonCreationDispatch();
  const {
    user, tutorId, tutorName,
    introMeetingChoice, introDate, introTime, eventName,
    subjectChoice, subjectOther, levelChoice, levelOther,
    lessonDate, lessonTime, lessonEndTime, lessonTitle,
    recursChoice, frequency, occurrenceCount, lessons, seriesName,
    isEditingSeriesName, editingLessonId, clientSecret,
    totalAmount, pendingLessonIds, tutorHourlyRate,
  } = state;

  // --- Hooks ---
  const { createEvents, isLoading: isCreatingEvents } = useEvents();
  const { prepareBooking, isPreparingBooking } = usePayment();
  const { addToast, updateToast } = useToast();
  const axios = useContext(AxiosContext);
  const queryClient = useQueryClient();

  // --- Fetch tutor's busy times ---
  useEffect(() => {
    if (!tutorId) return;
    let cancelled = false;
    axios.get(endpoints.busyTimes(tutorId))
      .then(res => {
        if (!cancelled) dispatch({ type: 'SET_TUTOR_BUSY_SLOTS', payload: res.data as BusySlot[] });
      })
      .catch(() => {}); // non-fatal
    return () => { cancelled = true; };
  }, [tutorId, axios, dispatch]);

  // --- Derived State (Calculations) ---
  const selectedSubject = subjectChoice === OTHER ? subjectOther.trim() : (subjectChoice ?? '');
  const selectedLevel = levelChoice === OTHER ? levelOther.trim() : (levelChoice ?? '');
  
  const subjectValid = !!selectedSubject;
  const levelValid = !!selectedLevel;

  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [isStripeReady, setIsStripeReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);



  const tz = state.activeInputTz;

  // Helper: check if a date+time slot has a conflict with existing bookings
  const hasConflict = (date: string, time: string, endTime: string): boolean => {
    if (!date || !time || !endTime) return false;
    try {
      const newStart = wallClockToISO(date, time, tz);
      const newEnd = wallClockEndToISO(date, time, endTime, tz);
      const parentHit = state.existingEvents.some(ev => ev.startAt < newEnd && ev.endAt > newStart);
      if (parentHit) return true;
      return state.tutorBusySlots.some(s => s.startAt < newEnd && s.endAt > newStart);
    } catch { return false; }
  };

  // For intro call conflict check: calculate end time as start + 30 mins
  const introEndTime = useMemo(() => {
    if (!introTime) return '';
    const [h, m] = introTime.split(':').map(Number);
    const totalMins = h * 60 + m + 15;
    return `${String(Math.floor(totalMins / 60) % 24).padStart(2, '0')}:${String(totalMins % 60).padStart(2, '0')}`;
  }, [introTime]);
  const canProceedFromStep0 = introMeetingChoice === 'no' || (introMeetingChoice === 'yes' && introDate && introTime && new Date(wallClockToISO(introDate, introTime, tz)).getTime() > Date.now() && !hasConflict(introDate, introTime, introEndTime));
  const canProceedFromStep1 = subjectValid && levelValid;
  const canProceedFromStep2 = Boolean(
    lessonDate && lessonTime && lessonEndTime && lessonEndTime !== lessonTime &&
    new Date(wallClockToISO(lessonDate, lessonTime, tz)).getTime() > Date.now() &&
    !hasConflict(lessonDate, lessonTime, lessonEndTime)
  );
  const canProceedFromStep3 = recursChoice !== null;
  const canProceedFromStep4 = Boolean(frequency && occurrenceCount >= 1);

  // --- Effects ---
  // This effect sets the event name for the intro call (Step 0)
  useEffect(() => {
    if (introMeetingChoice === 'yes') {
      dispatch({ type: 'SET_EVENT_NAME', payload: `Intro call on ${introDate}` });
    } else {
      dispatch({ type: 'SET_EVENT_NAME', payload: '' });
    }
  }, [introMeetingChoice, introDate, dispatch]);

  // This effect sets the *base* lesson title (used in Step 5)
  useEffect(() => {
    if (selectedSubject && selectedLevel) {
      const baseTitle = `${selectedLevel} ${selectedSubject} lesson`;
      dispatch({ type: 'SET_LESSON_TITLE', payload: baseTitle });
    }
  }, [selectedSubject, selectedLevel, dispatch]);

  // This effect calculates the default end time (Step 2)
  useEffect(() => {
    if (lessonTime && lessonDate) {
      const startTime = new Date(`${lessonDate}T${lessonTime}`);
      if (!isNaN(startTime.getTime())) {
        startTime.setHours(startTime.getHours() + 1);
        const endHours = String(startTime.getHours()).padStart(2, '0');
        const endMinutes = String(startTime.getMinutes()).padStart(2, '0');
        const newEndTime = `${endHours}:${endMinutes}`;
        dispatch({ type: 'SET_LESSON_END_TIME', payload: newEndTime });
      }
    }
  }, [lessonTime, lessonDate, dispatch]);
  
  // This effect sets the series name *once* when moving to Step 5
  useEffect(() => {
    if (step === 5 && lessons.length > 1 && !state.seriesName) {
      dispatch({ type: 'SET_SERIES_NAME', payload: `${selectedLevel} ${selectedSubject} lessons`});
    }
  }, [step, lessons.length, state.seriesName, selectedLevel, selectedSubject, dispatch]);

  const calculateLessonDuration = (startTime: string, endTime: string) : number => {
    if (!startTime || !endTime) return 0;
    const start = new Date(`1970-01-01T${startTime}:00`);
    const end = new Date(`1970-01-01T${endTime}:00`);

    if(isNaN(start.getTime()) || isNaN(end.getTime())) return 0;

    let diffMs = end.getTime() - start.getTime();

    if (diffMs < 0) {
      diffMs += 24 * 60 * 60 * 1000; 
    }

    const diffHours = diffMs / (1000 * 60 * 60);
    return diffHours
  }
  // --- Handlers ---
  const handleClose = () => {
    dispatch({ type: 'RESET', payload: initialState });
    setStep(0);
    onClose();
  };

  const confirmIntro = async () => {
    if (introMeetingChoice !== 'yes' || !tutorId) return;
    const tz = state.activeInputTz;
    const startIso = wallClockToISO(introDate, introTime, tz);
    const startDt = new Date(startIso);
    if (isNaN(startDt.getTime())) return;
    const endDt = new Date(startDt.getTime() + 15 * 60 * 1000);

    const payload = {
      tutorId: tutorId,
      parentId: user.id,
      eventType: 'intro-call' as const,
      title: eventName,
      startAt: startIso,
      endAt: endDt.toISOString(),
      status: 'confirmed'
    }
    try {
      await createEvents([payload]);
      handleClose();
    } catch (error) {
      console.error('Failed to create intro call: ', error);
    }
  };

  const handleCreatePendingLessonAndProceed = async () => {
    if (!lessons.length || !tutorId) return;
    setIsLoading(true);

    let newTotalAmountInPence = 0;

    for (const lesson of lessons) {
      const durationHours = calculateLessonDuration(lesson.time, lesson.endTime);
      const lessonPrice = durationHours * tutorHourlyRate;
      newTotalAmountInPence += lessonPrice;
    }

    // Add 25% platform fee, rounded up to the nearest pound (100 pence)
    const withMarkup = newTotalAmountInPence * 1.25;
    const finalTotalAmount = Math.ceil(withMarkup / 100) * 100;

    try {
      dispatch({ type: 'SET_TOTAL_AMOUNT', payload: finalTotalAmount });

      // Only create a PaymentIntent — no lessons in DB yet
      const { clientSecret } = await prepareBooking({
        amount: finalTotalAmount,
      });

      dispatch({ type: 'SET_CLIENT_SECRET', payload: clientSecret });
      setStep(6);
    } catch (error) {
      addToast({ variant: 'error', title: 'Could not initialise payment.' });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleConfirmPayment = async (setupIntentId: string) => {
    if (!lessons.length || !tutorId) {
      addToast({ variant: 'error', title: 'Could not find lessons to confirm.' });
      return;
    }

    // Build the event payloads from current state
    const seriesId = lessons.length > 1 ? uuidv4() : null;
    const eventTz = state.activeInputTz;
    const eventPayloads = lessons.map(l => ({
      tutorId,
      parentId: user.id,
      eventType: 'lesson' as const,
      title: l.title,
      startAt: wallClockToISO(l.date, l.time, eventTz),
      endAt: wallClockEndToISO(l.date, l.time, l.endTime, eventTz),
      seriesId,
      seriesTitle: lessons.length > 1 ? seriesName : null,
    }));

    const toastId = `booking-${Date.now()}`;
    addToast({ id: toastId, variant: 'loading', title: 'Creating your lessons...' });
    handleClose();

    try {
      await axios.post(endpoints.confirmPayment, {
        setupIntentId,
        events: eventPayloads,
        totalAmount: state.totalAmount,
      });
      queryClient.invalidateQueries({ queryKey: ['events', user.id] });
      updateToast(toastId, { variant: 'success', title: 'Lessons booked successfully!' });
    } catch (error: any) {
      console.error('Failed to confirm lessons after payment:', error);
      const msg = error?.response?.data?.message || 'Booking failed — please contact support.';
      updateToast(toastId, { variant: 'error', title: msg });
    }
  };


  // --- Step Navigation ---
  const handleNextFromStep0 = () => {
    if (introMeetingChoice === 'no') setStep(1);
  };
  
  const handleNextFromStep1 = () => setStep(2);

  const handleNextFromStep2 = () => setStep(3);
  
  const handleNextFromStep3 = () => {
    if (recursChoice === 'yes') {
      setStep(4);
    } else if (recursChoice === 'no') {
      const items = generateOccurrences(lessonDate, lessonTime, lessonEndTime, lessonTitle, null, 1, true);
      dispatch({ type: 'SET_LESSONS', payload: items });
      setStep(5);
    }
  };

  const handleNextFromStep4 = () => {
    if (!frequency) return;
    const items = generateOccurrences(lessonDate, lessonTime, lessonEndTime, lessonTitle, frequency, occurrenceCount);
    dispatch({ type: 'SET_LESSONS', payload: items });
    setStep(5);
  };
  
  const minStep = skipIntro ? 1 : 0;
  const handleBack = () => {
    if (step <= minStep) {
      if (onBackFromStart) onBackFromStart();
      return;
    }
    if (step === 5) {
      if (recursChoice === 'yes') setStep(4);
      else setStep(3);
      return;
    }
    setStep((s) => (Math.max(minStep, (s - 1)) as Step));
  };

  // --- Render Functions ---

  const renderCurrentStep = () => {
    switch (step) {
      case 0: return <Step0_IntroChoice />; // This component will use the context hooks
      case 1: return <Step1_SubjectLevel />; // This component will use the context hooks
      case 2: return <Step2_FirstLesson />; // <Step2_FirstLesson />
      case 3: return <Step3_RecursChoice />; // <Step3_RecursChoice />
      case 4: return <Step4_Frequency />; // <Step4_Frequency />
      case 5: return <Step5_LessonPlan />; // <Step5_LessonPlan />
      case 6: 
      return (
        <Step6_Payment
          formId={PAYMENT_FORM_ID}
          onPaymentSuccess={handleConfirmPayment}
          onProcessingChange={setIsPaymentProcessing}
          onReadyChange={setIsStripeReady}
        />
      );
    // --------------------------
     default: return null;
    }
  };

  const renderFooter = () => {
    if (step === 0) {
      return introMeetingChoice ? (
        <ModalFooter>
          <SecondaryButton onClick={handleClose}>Cancel</SecondaryButton>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {introMeetingChoice === 'no' ? (
              <PrimaryButton
                onClick={handleNextFromStep0}
                disabled={!canProceedFromStep0}
              >
                Next
              </PrimaryButton>
            ) : (
              <PrimaryButton
                onClick={confirmIntro}
                disabled={!canProceedFromStep0 || isCreatingEvents} // Use canProceed + hook loading
              >
                {isCreatingEvents ? 'Confirming...' : 'Confirm Call'}
              </PrimaryButton>
            )}
          </div>
        </ModalFooter>
      ) : null;
    }

    if (step === 1) {
      const showBack = !skipIntro || !!onBackFromStart;
      return (
        <ModalFooter>
          {showBack ? (
            <SecondaryButton onClick={handleBack}>Back</SecondaryButton>
          ) : <div />}
          <PrimaryButton onClick={handleNextFromStep1} disabled={!canProceedFromStep1}>
            Next
          </PrimaryButton>
        </ModalFooter>
      );
    }

    if (step === 2) {
      return (
        <ModalFooter>
          <SecondaryButton onClick={handleBack}>Back</SecondaryButton>
          <PrimaryButton onClick={handleNextFromStep2} disabled={!canProceedFromStep2}>
            Next
          </PrimaryButton>
        </ModalFooter>
      );
    }
  

    if (step === 3) {
      return (
        <ModalFooter>
          <SecondaryButton onClick={handleBack}>Back</SecondaryButton>
          <PrimaryButton onClick={handleNextFromStep3} disabled={!canProceedFromStep3}>
            Next
          </PrimaryButton>
        </ModalFooter>
      );
    }

    if (step === 4) {
      return (
        <ModalFooter>
          <SecondaryButton onClick={handleBack}>Back</SecondaryButton>
          <PrimaryButton onClick={handleNextFromStep4} disabled={!canProceedFromStep4}>
            Next
          </PrimaryButton>
        </ModalFooter>
      );
    }

    if (step === 5) {
      return (
        <ModalFooter>
          <SecondaryButton onClick={handleBack}>Back</SecondaryButton>
          <PrimaryButton
            onClick={handleCreatePendingLessonAndProceed}
            disabled={lessons.length === 0 || isPreparingBooking || isLoading || lessons.some(l => hasConflict(l.date, l.time, l.endTime))}
          >
            {isPreparingBooking || isLoading ? 'Initialising...' : 'Proceed to Payment'}
          </PrimaryButton>
        </ModalFooter>
      );
    }

    if (step === 6) {
      return (
        <ModalFooter>
          <SecondaryButton
            onClick={() => {
              dispatch({ type: 'SET_CLIENT_SECRET', payload: '' });
              setStep(5);
            }}
            disabled={isPaymentProcessing}
          >
            Back to Plan
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            form={PAYMENT_FORM_ID} // Use const
            disabled={isPaymentProcessing || !isStripeReady} // Use local state
          >
            {isPaymentProcessing ? 
            'Processing...' :
              `Pay ${(totalAmount / 100).toLocaleString('en-GB', {style: 'currency', currency: 'GBP'})}` // Use global state
            }
          </PrimaryButton>
        </ModalFooter>
      )  
    }
  }


  return (
    <ModalOverlay isOpen={true} onClick={handleClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ContentWrapper>
          <ModalHeader>
            {toReadableHeader(step)}
            <CloseButton onClick={handleClose}>
              <XCircleFill size={20} />
            </CloseButton>
          </ModalHeader>
          <StepContent>
            {renderCurrentStep()}
          </StepContent>
        </ContentWrapper>
        {renderFooter()}
      </ModalContainer>
    </ModalOverlay>
  );
};


/**
 * This is the main exported component.
 * It sets up the Provider and renders the ModalContent
 * only when `isOpen` is true.
 */
export const LessonCreationModal: React.FC<LessonCreationModalProps> = ({
  isOpen,
  onClose,
  tutorId,
  tutorName,
  tutorHourlyRate,
  tutorTimezone,
  tutorAvailability,
  existingEvents,
  tutorOffersIntroCall,
  onBackFromStart,
  initialInputTz,
  user,
}) => {
  const tz = tutorTimezone || 'Europe/London';
  const avail = tutorAvailability ?? null;
  const evts = existingEvents ?? [];

  // Skip intro step if tutor doesn't offer it or there's already any event with this tutor
  const hasExistingWithTutor = evts.some(ev => ev.tutorId === tutorId || ev.parentId === tutorId);
  const skipIntro = !tutorOffersIntroCall || hasExistingWithTutor;

  const initialState = useMemo(() => {
    const state = getInitialState(user, tutorId, tutorName, tutorHourlyRate, tz, avail, evts);
    if (initialInputTz) state.activeInputTz = initialInputTz;
    return state;
  }, [user, tutorId, tutorName, tutorHourlyRate, tz, avail, evts, initialInputTz]
  );

  if (!isOpen) {
    return null;
  }

  return (
    <LessonCreationProvider
      user={user}
      tutorId={tutorId}
      tutorName={tutorName}
      tutorHourlyRate={tutorHourlyRate}
      tutorTimezone={tz}
      tutorAvailability={avail}
      existingEvents={evts}
    >
      <ModalContent onClose={onClose} initialState={initialState} skipIntro={skipIntro} onBackFromStart={onBackFromStart} />
    </LessonCreationProvider>
  );
};

